import React from "react";
import { BookOpen, Check } from "lucide-react";
import { MathText, cn } from "../../ui";
import { DIFFICULTIES, answerKind, letterOf, marksSummary, typeLabel } from "./questionRules";

/**
 * QuestionPreview — the question as a student sees it in an exam, with the
 * answer key marked for the author (`showAnswer`). The Review step and the
 * live column beside the Question, Answer and Marks steps both render it, so
 * what the author checks is what they were looking at while writing.
 */
export default function QuestionPreview({ form, passage, showAnswer = true }) {
  const kind = answerKind(form.questionType);
  const difficulty = DIFFICULTIES.find((d) => d.value === form.difficulty)?.label;
  const text = String(form.questionText || "").trim();

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <p className="tw-m-0 tw-text-xs tw-text-content-subtle">
        {[typeLabel(form.questionType), marksSummary(form).split(" · ")[0], difficulty].filter(Boolean).join(" · ")}
      </p>

      {form.passage && (
        <p className="tw-m-0 tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-content-muted">
          <BookOpen size={14} aria-hidden="true" className="tw-shrink-0 tw-text-primary" />
          About the passage “{passage?.title || "…"}”, shown beside it
        </p>
      )}

      {text ? (
        <MathText
          as="p"
          text={form.questionText}
          className="tw-m-0 tw-whitespace-pre-wrap tw-text-[0.9375rem] tw-leading-relaxed tw-text-content"
        />
      ) : (
        <p className="tw-m-0 tw-text-sm tw-italic tw-text-content-subtle">Your question appears here.</p>
      )}

      {form.imageUrl && (
        <img
          src={form.imageUrl}
          alt="Question figure"
          className="tw-max-h-56 tw-max-w-full tw-self-start tw-rounded-md tw-border tw-border-solid tw-border-line tw-object-contain"
        />
      )}

      {form.questionType === "code_conceptual" && form.codeSnippet && (
        <pre className="tw-m-0 tw-overflow-x-auto tw-rounded-md tw-bg-muted tw-p-3 tw-font-mono tw-text-xs tw-text-content">
          {form.codeSnippet}
        </pre>
      )}

      {kind === "options" && (
        <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
          {form.options.map((o, i) => {
            const correct = showAnswer && o.isCorrect;
            return (
              <li
                key={o._id || i}
                className={cn(
                  "tw-flex tw-items-start tw-gap-2.5 tw-rounded-md tw-border tw-border-solid tw-px-3 tw-py-2",
                  correct ? "tw-border-success/60 tw-bg-success/[0.07]" : "tw-border-line tw-bg-surface-raised",
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "tw-inline-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-xs tw-font-semibold",
                    correct ? "tw-bg-success tw-text-on-status" : "tw-bg-muted tw-text-content",
                  )}
                >
                  {correct ? <Check size={13} strokeWidth={3} /> : letterOf(i)}
                </span>
                <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1.5 tw-pt-0.5">
                  {String(o.text || "").trim()
                    ? <MathText text={o.text} className="tw-text-sm tw-text-content" />
                    : !o.imageUrl && <span className="tw-text-sm tw-italic tw-text-content-subtle">Option {letterOf(i)}</span>}
                  {o.imageUrl && (
                    <img
                      src={o.imageUrl}
                      alt={`Option ${letterOf(i)}`}
                      className="tw-max-h-28 tw-max-w-full tw-self-start tw-rounded-sm tw-object-contain"
                    />
                  )}
                </span>
                {correct && <span className="tw-sr-only">(correct)</span>}
              </li>
            );
          })}
        </ol>
      )}

      {kind === "text" && (
        <div className="tw-flex tw-flex-col tw-gap-2">
          <div className="tw-rounded-md tw-border tw-border-dashed tw-border-line tw-px-3 tw-py-2 tw-text-sm tw-text-content-subtle">
            Students type their answer here
          </div>
          {showAnswer && (
            <p className="tw-m-0 tw-text-xs tw-text-content-muted">
              Accepted, in any case:{" "}
              <span className="tw-font-medium tw-text-content">
                {form.acceptedAnswers.filter((a) => String(a).trim()).join(" · ") || "none yet"}
              </span>
            </p>
          )}
        </div>
      )}

      {kind === "numeric" && (
        <div className="tw-flex tw-flex-col tw-gap-2">
          <div className="tw-w-40 tw-rounded-md tw-border tw-border-dashed tw-border-line tw-px-3 tw-py-2 tw-text-sm tw-text-content-subtle">
            A number
          </div>
          {showAnswer && (
            <p className="tw-m-0 tw-text-xs tw-text-content-muted">
              Correct:{" "}
              <span className="tw-font-medium tw-text-content">
                {form.numericAnswer === "" ? "not set yet" : form.numericAnswer}
                {Number(form.numericTolerance) > 0 ? ` (± ${form.numericTolerance})` : ""}
              </span>
            </p>
          )}
        </div>
      )}

      {showAnswer && String(form.explanation || "").trim() && (
        <div className="tw-rounded-md tw-bg-muted/60 tw-px-3 tw-py-2">
          <p className="tw-m-0 tw-text-xs tw-font-semibold tw-text-content">Explanation</p>
          <MathText as="p" text={form.explanation} className="tw-m-0 tw-whitespace-pre-wrap tw-text-[0.8125rem] tw-text-content-muted" />
        </div>
      )}
    </div>
  );
}
